import { useSpring } from 'framer-motion';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import SegmentedControl from '../segmented-control';
import EditMode from './components/edit-mode';
import PreviewMode from './components/preview-mode';

export type MarkdownEditorProps = {
  value?: string;
  onChange?: (value: string) => void;
  height?: number;
  expandedHeight?: number;
  expanded?: boolean;
  disabled?: boolean;
  placeholder?: string;
  hasError?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
};

export type MarkdownEditorRef = {
  focus: () => void;
};

type ViewMode = 'edit' | 'preview';
const DEFAULT_HEIGHT = 200;
const EXPANDED_HEIGHT_MULTIPLIER = 3;

const MarkdownEditor = forwardRef<MarkdownEditorRef, MarkdownEditorProps>(
  (
    {
      value = '',
      onChange,
      height = DEFAULT_HEIGHT,
      expandedHeight: expandedHeightProp,
      disabled = false,
      placeholder,
      hasError = false,
      onFocus,
      onBlur,
      expanded = false,
    },
    ref,
  ) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [viewMode, setViewMode] = useState<ViewMode>('edit');
    const [isFocused, setIsFocused] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [isExpanded, setIsExpanded] = useState(expanded);
    const expandedHeight = expandedHeightProp ?? height * EXPANDED_HEIGHT_MULTIPLIER;
    useImperativeHandle(ref, () => ({
      focus: () => {
        textareaRef.current?.focus();
      },
    }));

    const defaultHeight = expanded ? expandedHeight : height;
    const animatedHeight = useSpring(defaultHeight, {
      stiffness: 300,
      damping: 30,
    });
    const [currentHeight, setCurrentHeight] = useState(defaultHeight);
    const shouldShowExpandButton = (isHovered || isFocused) && viewMode === 'edit';

    useEffect(() => {
      const targetHeight = isExpanded ? expandedHeight : height;
      animatedHeight.set(targetHeight);
    }, [isExpanded, height, expandedHeight, animatedHeight]);

    useEffect(() => {
      const unsubscribe = animatedHeight.on('change', latest => {
        setCurrentHeight(Math.round(latest));
      });
      return unsubscribe;
    }, [animatedHeight]);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange?.(e.target.value);
    };

    const handleFocus = () => {
      setIsFocused(true);
      onFocus?.();
    };

    const handleBlur = () => {
      setIsFocused(false);
      onBlur?.();
    };

    return (
      // biome-ignore lint/a11y/noStaticElementInteractions: hover detection for expand button visibility
      <div
        className="flex flex-col gap-2 w-full"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <SegmentedControl
          aria-label="Editor mode"
          size="compact"
          options={[
            { label: 'Edit', value: 'edit' },
            { label: 'Preview', value: 'preview' },
          ]}
          value={viewMode}
          onChange={setViewMode}
        />

        <div className="flex flex-col w-full">
          {viewMode === 'edit' ? (
            <EditMode
              textareaRef={textareaRef}
              value={value}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              disabled={disabled}
              placeholder={placeholder}
              hasError={hasError}
              currentHeight={currentHeight}
              shouldShowExpandButton={shouldShowExpandButton}
              isExpanded={isExpanded}
              onToggleExpand={() => setIsExpanded(!isExpanded)}
            />
          ) : (
            <PreviewMode value={value} height={currentHeight} maxHeight={expandedHeight} hasError={hasError} />
          )}
        </div>
      </div>
    );
  },
);

MarkdownEditor.displayName = 'MarkdownEditor';

export default MarkdownEditor;
